import { useState } from "react";
import { useEditorStore } from "@/store";

interface CoordinatesFieldProps {
  label: string;
  /** Array of [lng, lat] coordinate pairs — typically 4 corners */
  value: number[][] | undefined;
  onChange: (value: number[][]) => void;
  description?: string;
}

const CORNER_LABELS = ["Top-left", "Top-right", "Bottom-right", "Bottom-left"];

/**
 * Editor for geographic corner coordinates (image/video source positioning).
 * Shows 4 corner inputs with lng/lat, plus a "Use current viewport" helper.
 */
export function CoordinatesField({ label, value, onChange, description }: CoordinatesFieldProps) {
  const [showTip, setShowTip] = useState(false);
  const viewport = useEditorStore((s) => s.viewport);
  const coords = value && value.length === 4
    ? value as [number, number][]
    : [[0, 0], [0, 0], [0, 0], [0, 0]] as [number, number][];

  const update = (cornerIdx: number, coordIdx: number, val: number) => {
    const next = coords.map((c) => [...c]);
    const corner = next[cornerIdx];
    if (corner) corner[coordIdx] = val;
    onChange(next);
  };

  const useCurrentViewport = () => {
    const degW = 360 / Math.pow(2, viewport.zoom) / 2;
    const degH = degW * 0.6;
    const lng = viewport.center[0];
    const lat = viewport.center[1];
    onChange([
      [lng - degW, lat + degH],  // TL
      [lng + degW, lat + degH],  // TR
      [lng + degW, lat - degH],  // BR
      [lng - degW, lat - degH],  // BL
    ]);
  };

  return (
    <div className="group/field rounded-radius-sm px-1.5 py-1">
      <div className="flex items-center gap-2 mb-2">
        <div
          className="relative flex shrink-0 items-center gap-1"
          onMouseEnter={() => { if (description) setShowTip(true); }}
          onMouseLeave={() => setShowTip(false)}
        >
          <span className="text-[11px] text-text-secondary">{label}</span>
          {showTip && description && (
            <div className="absolute left-0 bottom-full z-50 mb-1 w-52 rounded-radius-sm border border-border-subtle bg-surface-overlay px-2.5 py-1.5 text-[10px] leading-relaxed text-text-secondary shadow-md">
              {description}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={useCurrentViewport}
          className="rounded-radius-sm border border-accent bg-accent/10 px-2 py-0.5 text-[9px] font-medium text-accent hover:bg-accent hover:text-accent-text transition-colors"
        >
          Use current viewport
        </button>
      </div>

      <div className="space-y-1">
        {CORNER_LABELS.map((cornerLabel, i) => (
          <div key={cornerLabel} className="flex items-center gap-1.5">
            <span className="w-20 text-[9px] text-text-muted">{cornerLabel}</span>
            <div className="flex items-center gap-0.5">
              <span className="text-[8px] text-text-muted">lng</span>
              <input
                type="number"
                value={coords[i]?.[0] ?? 0}
                onChange={(e) => update(i, 0, Number(e.target.value))}
                step={0.001}
                className="h-5 w-20 rounded-radius-sm border border-border-subtle bg-surface-base px-1 text-[9px] text-text-primary focus:border-accent focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-0.5">
              <span className="text-[8px] text-text-muted">lat</span>
              <input
                type="number"
                value={coords[i]?.[1] ?? 0}
                onChange={(e) => update(i, 1, Number(e.target.value))}
                step={0.001}
                className="h-5 w-20 rounded-radius-sm border border-border-subtle bg-surface-base px-1 text-[9px] text-text-primary focus:border-accent focus:outline-none"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-1.5 text-[8px] text-text-muted">
        Corners clockwise: top-left → top-right → bottom-right → bottom-left
      </div>
    </div>
  );
}
